/**
 * 性能悬浮层（仅当 URL 带 `?stats=1` 时出现）
 *
 * 为什么需要它：验收标准里写着"能在一台千元安卓机浏览器上 60fps 流畅玩"，
 * 但"感觉挺流畅"不是数据。这个悬浮层把帧率、最长帧、掉帧数摆在屏幕上，
 * 让真机验收有可量化的依据；测完把参数去掉就完全不存在，普通玩家看不到。
 *
 * 它测的是**整页的帧节奏**（rAF 间隔），不只是地图画布 —— 因为卡顿往往来自
 * 布局/DOM 重排而不是 Canvas 本身。
 */

const SAMPLE_WINDOW_MS = 500;
/** 超过这个间隔视为掉了一帧（60fps 单帧 16.7ms，给一倍余量） */
const LONG_FRAME_MS = 34;

export interface StatsSample {
  regions: number;
  lit: number;
  mist: number;
  canvasWidth: number;
  canvasHeight: number;
}

export interface StatsOverlay {
  update(sample: StatsSample): void;
  destroy(): void;
}

/** 未带 ?stats=1 时返回 null，调用方用完即忘 */
export function mountStats(getSample: () => StatsSample): StatsOverlay | null {
  const params = new URLSearchParams(window.location.search);
  if (!params.has("stats")) return null;

  const root = document.createElement("div");
  root.className = "stats";
  root.setAttribute("role", "status");
  root.setAttribute("aria-live", "off");
  document.body.append(root);

  let frames = 0;
  let windowStart = performance.now();
  let lastFrame = windowStart;
  let longestFrame = 0;
  let longFrames = 0;
  let worstFps = Infinity;
  let frameHandle = 0;

  const tick = (now: number): void => {
    frames += 1;
    const delta = now - lastFrame;
    lastFrame = now;
    if (delta > longestFrame) longestFrame = delta;
    if (delta > LONG_FRAME_MS) longFrames += 1;

    const elapsed = now - windowStart;
    if (elapsed >= SAMPLE_WINDOW_MS) {
      const fps = (frames * 1000) / elapsed;
      if (fps < worstFps) worstFps = fps;
      const sample = getSample();
      const dpr = window.devicePixelRatio || 1;
      root.innerHTML = [
        `<strong>${fps.toFixed(0)} fps</strong>`,
        `最低 ${worstFps === Infinity ? "-" : worstFps.toFixed(0)}`,
        `最长帧 ${longestFrame.toFixed(0)}ms`,
        `掉帧 ${longFrames}`,
        `区域 ${sample.lit}/${sample.regions} 已点亮`,
        `迷雾 ${Math.round(sample.mist)}`,
        `画布 ${sample.canvasWidth}×${sample.canvasHeight}@${dpr}x`,
      ]
        .map((line) => `<span>${line}</span>`)
        .join("");
      frames = 0;
      windowStart = now;
      longestFrame = 0;
      longFrames = 0;
    }
    frameHandle = requestAnimationFrame(tick);
  };
  frameHandle = requestAnimationFrame(tick);

  return {
    update() {
      // 数值来自 rAF 采样，这里不需要额外动作；保留接口是为了将来接"按操作计数"的指标
    },
    destroy() {
      cancelAnimationFrame(frameHandle);
      root.remove();
    },
  };
}
